/** An explicit http/https scheme, matched case-insensitively ("HTTP://" counts). */
const HTTP_SCHEME_PATTERN = /^https?:\/\//i;

/** Scheme prepended when the user typed a bare host such as "www.example.com". */
const DEFAULT_SCHEME = 'https://';

/** Leading slashes of a protocol-relative value ("//example.com"). */
const LEADING_SLASHES_PATTERN = /^\/+/;

/**
 * Normalize a user-typed URL so a bare host is accepted and stored with a scheme.
 *
 * Users routinely type "www.example.com" rather than the full URL, so a value
 * with no http(s) scheme gets `https://` prepended. Blank input stays blank so
 * optional URL fields keep their "not provided" meaning. Leading slashes are
 * dropped first, so a protocol-relative "//example.com" becomes
 * "https://example.com" rather than "https:////example.com". Any other scheme
 * ("javascript:", "data:", "ftp:") is not recognised as http(s) and is
 * prefixed too, which leaves it a harmless https host string. Pure.
 *
 * @param input The raw value from a URL input.
 * @returns The trimmed value, prefixed with `https://` when it had no http(s)
 *   scheme; `''` when the input was blank.
 */
export function normalizeUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  if (HTTP_SCHEME_PATTERN.test(trimmed)) return trimmed;
  return `${DEFAULT_SCHEME}${trimmed.replace(LEADING_SLASHES_PATTERN, '')}`;
}

/**
 * True when `value` parses as a URL whose host has a domain suffix
 * ("example.com", not "example"). Prepending `https://` makes almost any typed
 * word parse as a URL, so this is what still catches "acme" as a typo client
 * side; the Backend's `@IsUrl()` requires a TLD and would otherwise 400. Pure.
 *
 * @param value A URL string, typically already passed through `normalizeUrl`.
 * @returns Whether it parses and its hostname contains an inner dot.
 */
export function hasDomainHost(value: string): boolean {
  let hostname: string;
  try {
    hostname = new URL(value).hostname;
  } catch {
    // Unparseable input is simply "not a URL" - the caller reports it as a field error.
    return false;
  }
  const dot = hostname.indexOf('.');
  return dot > 0 && dot < hostname.length - 1 && !hostname.endsWith('.');
}

/**
 * The parsed `href` when `value` is, as a whole, one http or https URL;
 * otherwise null. Decides whether text from the API may become a link: any other
 * scheme ("javascript:", "data:", "mailto:") or text that merely contains a
 * URL stays plain text. Pure.
 *
 * @param value Raw text, e.g. an event's join link or address.
 */
export function toHttpHref(value: string): string | null {
  const trimmed = value.trim();
  if (!HTTP_SCHEME_PATTERN.test(trimmed) || /\s/.test(trimmed)) return null;
  try {
    const { protocol, href } = new URL(trimmed);
    return protocol === 'http:' || protocol === 'https:' ? href : null;
  } catch {
    // Unparseable text is not a link.
    return null;
  }
}
